<template>
  <div>
    <div class="space-y-4">
      <!-- Page header -->
      <div class="flex items-center gap-3">
        <span class="flex items-center justify-center w-10 h-10 rounded-xl bg-taxiye-100 text-taxiye-500">
          <span class="material-icons-round">history</span>
        </span>
        <div>
          <h2 class="text-lg font-semibold text-gray-800">SOS History</h2>
          <p class="text-sm text-gray-500">Review past SOS alerts.</p>
        </div>
      </div>
      <div class="p-4 bg-white border border-gray-200 rounded-2xl shadow-sm">
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center space-x-1 text-gray-500">
            <span class="text-base material-icons-round">filter_list</span>
            <span class="text-xs font-semibold tracking-widest uppercase">Filters</span>
          </div>
        </div>

        <div class="flex flex-wrap items-end gap-4">
          <!-- Search -->
          <div class="relative w-full sm:w-72">
            <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Search</label>
            <div class="relative mt-1.5">
              <span class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <span class="text-base text-gray-500 material-icons-round">search</span>
              </span>
              <input
                v-model="searchText"
                @keyup="search"
                class="w-full h-10 pl-9 pr-3 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="Search driver or passenger..."
              />
            </div>
            <transition
              enter-active-class="transition duration-100 ease-out"
              enter-class="transform scale-95 opacity-0"
              enter-to-class="transform scale-100 opacity-100"
              leave-active-class="transition duration-75 ease-in"
              leave-class="transform scale-100 opacity-100"
              leave-to-class="transform scale-95 opacity-0"
            >
              <div
                v-if="drivers.length || passengers.length"
                class="absolute inset-x-0 z-50 mt-2"
              >
                <div class="py-1 overflow-y-auto bg-white border border-gray-100 shadow-lg rounded-xl max-h-64">
                  <div v-if="drivers.length">
                    <p class="px-3 py-1 text-xs font-semibold tracking-wider text-gray-400 uppercase">Drivers</p>
                    <ul>
                      <li
                        v-for="(result, index) in drivers"
                        :key="`driver-${index}`"
                        class="px-3 py-2 cursor-pointer transition-colors duration-150 hover:bg-taxiye-50"
                        @click="filterSos('driver', result)"
                      >
                        <p class="text-sm font-semibold text-gray-800">
                          {{ result.firstName + " " + result.lastName }}
                        </p>
                        <p class="text-xs text-gray-500">{{ result.phoneNumber }}</p>
                      </li>
                    </ul>
                  </div>
                  <div v-if="passengers.length">
                    <p class="px-3 py-1 text-xs font-semibold tracking-wider text-gray-400 uppercase">Passengers</p>
                    <ul>
                      <li
                        v-for="(result, index) in passengers"
                        :key="`passenger-${index}`"
                        class="px-3 py-2 cursor-pointer transition-colors duration-150 hover:bg-taxiye-50"
                        @click="filterSos('passenger', result)"
                      >
                        <p class="text-sm font-semibold text-gray-800">
                          {{ result.firstName + " " + result.lastName }}
                        </p>
                        <p class="text-xs text-gray-500">{{ result.phoneNumber }}</p>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </transition>
          </div>

          <!-- Type -->
          <div class="w-full sm:w-48">
            <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Type</label>
            <select
              class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-select rounded-xl focus:border-taxiye-300"
              v-model="type"
              @change="getSos"
            >
              <option value="all">All</option>
              <option value="passenger">Passenger</option>
              <option value="driver">Driver</option>
            </select>
          </div>
        </div>
      </div>

      <div class="mt-4 overflow-hidden bg-white border border-gray-200 rounded-xl shadow-sm">
        <div class="overflow-x-auto">
          <table class="min-w-full text-sm">
            <thead>
              <tr class="bg-gray-100 border-b border-gray-200">
                <th
                  v-for="col in ['Driver', 'Passenger', 'Type', 'Happened At', '']"
                  :key="`col-${col}`"
                  class="px-5 py-3 text-xs font-semibold tracking-wider text-left text-gray-500 uppercase whitespace-no-wrap"
                >
                  {{ col }}
                </th>
              </tr>
            </thead>
            <tbody class="relative" v-if="allSos.length">
              <tr
                v-for="(sos, index) in allSos"
                :key="sos._id"
                class="transition-colors duration-150 hover:bg-taxiye-50"
                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-100'"
              >
                <!-- Driver -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <router-link
                    v-if="sos.driver"
                    :to="{ path: '/drivers/' + sos.driver._id }"
                    class="flex items-center space-x-3 group"
                  >
                    <span
                      class="flex items-center justify-center flex-shrink-0 w-10 h-10 text-xs font-bold rounded-full"
                      :class="avatarClass(personLabel(sos.driver))"
                    >{{ initials(personLabel(sos.driver)) }}</span>
                    <div>
                      <p class="font-medium text-gray-900 group-hover:text-taxiye-600">
                        {{ personLabel(sos.driver, "Unknown") }}
                      </p>
                      <p class="text-xs text-gray-500">
                        {{ sos.driver.phoneNumber || "—" }}
                      </p>
                    </div>
                  </router-link>
                  <span v-else class="text-gray-400">—</span>
                </td>
                <!-- Passenger -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <div v-if="sos.passenger" class="flex items-center space-x-3">
                    <span
                      class="flex items-center justify-center flex-shrink-0 w-10 h-10 text-xs font-bold rounded-full"
                      :class="avatarClass(personLabel(sos.passenger))"
                    >{{ initials(personLabel(sos.passenger)) }}</span>
                    <div>
                      <p class="font-medium text-gray-900">
                        {{ personLabel(sos.passenger, "Unknown") }}
                      </p>
                      <p class="text-xs text-gray-500">
                        {{ sos.passenger.phoneNumber || "—" }}
                      </p>
                    </div>
                  </div>
                  <span v-else class="text-gray-400">—</span>
                </td>
                <!-- Type -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <span
                    class="inline-flex px-2 py-1 text-xs font-medium capitalize rounded-md"
                    :class="sos.type === 'driver' ? 'text-indigo-600 bg-indigo-50' : 'text-teal-700 bg-teal-100'"
                  >
                    {{ sos.type || "—" }}
                  </span>
                </td>
                <!-- Happened at -->
                <td class="px-5 py-4 text-gray-600 whitespace-no-wrap">
                  <div class="flex items-center space-x-2">
                    <i class="text-sm text-gray-400 material-icons-round">schedule</i>
                    <span>{{ formatDate(sos.createdAt) }}</span>
                  </div>
                </td>
                <!-- Actions -->
                <td class="px-5 py-4 text-right whitespace-no-wrap">
                  <a
                    v-if="sos.trip"
                    :href="'/rides/' + sos.trip + '/show'"
                    target="self"
                    title="View trip"
                    class="inline-flex items-center justify-center w-9 h-9 text-gray-500 rounded-lg transition-colors duration-150 hover:bg-gray-100 hover:text-taxiye-600 focus:outline-none"
                  >
                    <i class="material-icons-round">visibility</i>
                  </a>
                </td>
              </tr>
              <div
                v-if="loading"
                class="absolute inset-0 flex items-center justify-center w-full h-full bg-white bg-opacity-50"
              >
                <i class="text-5xl text-taxiye-500 material-icons-round spin">sync</i>
                <span class="invisible block">Loading...</span>
              </div>
            </tbody>
            <tbody v-else>
              <tr>
                <td colspan="5" class="py-16 text-center bg-white">
                  <div v-if="loading" class="inline-block">
                    <i class="text-5xl text-taxiye-500 material-icons-round spin">sync</i>
                    <span class="invisible block">Loading...</span>
                  </div>
                  <div v-else class="flex flex-col items-center text-gray-400">
                    <span class="flex items-center justify-center w-16 h-16 mb-3 rounded-full bg-gray-100">
                      <i class="text-3xl material-icons-round">inbox</i>
                    </span>
                    <span class="text-sm font-medium text-gray-500">No SOS history found</span>
                    <span class="mt-1 text-xs text-gray-400">Try adjusting your filters</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="flex items-end justify-between">
        <pagination
          class="flex space-x-2"
          :currentPage="currentPage"
          :total="total"
          @goto="goToPage"
          @setPerPage="setPerPage"
          :perPage="perPage"
          :start="start"
          :end="end"
          :count="paginator.count"
        ></pagination>
      </div>
    </div>
  </div>
</template>

<script>
import _ from "lodash";
import PaginationMixin from "@/mixins/Pagination";
import Pagination from "@/components/Pagination";

export default {
  mixins: [PaginationMixin],
  data() {
    return {
      allSos: [],
      loading: true,
      drivers: [],
      passengers: [],
      passengerId: "",
      driverId: "",
      searchText: "",
      type: "all",
    };
  },
  components: {
    Pagination,
  },
  created() {
    this.getSos();
  },
  watch: {
    currentPage() {
      this.getSos();
    },
    perPage() {
      this.getSos();
    },
  },
  methods: {
    personLabel(person, fallback = "") {
      if (!person) return fallback;
      const name = `${person.firstName || ""} ${person.lastName || ""}`.trim();
      if (!name) return fallback;
      return name
        .split(" ")
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
    },
    initials(name) {
      if (!name) return "?";
      return name
        .split(" ")
        .filter(Boolean)
        .map((part) => part[0])
        .slice(0, 2)
        .join("")
        .toUpperCase();
    },
    avatarClass(name) {
      const palette = [
        "bg-taxiye-100 text-taxiye-600",
        "bg-indigo-50 text-indigo-600",
        "bg-blue-50 text-blue-600",
        "bg-green-50 text-green-600",
        "bg-purple-50 text-purple-600",
        "bg-pink-50 text-pink-600",
      ];
      let hash = 0;
      for (let i = 0; i < (name || "").length; i++) {
        hash = (hash + name.charCodeAt(i)) % palette.length;
      }
      return palette[hash];
    },
    getSos() {
      this.loading = true;
      var path = `/sos?limit=${this.perPage}&page=${this.currentPage}&populate=["driver","passenger","vehicle"]`;

      if (this.passengerId) {
        path += "&passenger=" + this.passengerId;
      }

      if (this.type && this.type != "all") {
        path += "&type=" + this.type;
      }

      if (this.driverId) {
        path += "&driver=" + this.driverId;
      }

      this.$http
        .get(path)
        .then((response) => {
          this.allSos = response.data.data;
          this.paginator = {
            nextPage: response.data.nextPage,
            prevPage: response.data.prevPage,
            count: response.data.count,
          };
        })
        .catch((err) => console.log(err))
        .finally(() => (this.loading = false));
    },
    filterSos(type, data) {
      this.passengerId = "";
      this.driverId = "";
      this.drivers = [];
      this.passengers = [];

      if (type == "passenger") {
        this.passengerId = data._id;
      } else if (type == "driver") {
        this.driverId = data._id;
      }
      this.searchText =
        data.firstName +
        " " +
        data.lastName +
        ", " +
        data.phoneNumber +
        ", " +
        type;

      this.getSos();
    },
    search: _.debounce(function (e) {
      if (!e.target.value.trim()) {
        this.drivers = [];
        this.passengers = [];
        this.driverId = "";
        this.passengerId = "";
        this.getSos();
        return;
      }

      this.$http
        .get(`/drivers/search?limit=5&q=${e.target.value}`)
        .then((response) => {
          this.drivers = response.data;
        })
        .catch((err) => console.log(err));

      this.$http
        .get(`/users/search?limit=5&q=${e.target.value}`)
        .then((response) => {
          this.passengers = response.data;
        })
        .catch((err) => console.log(err));
    }, 500),
    formatDate(date) {
      return date ? date.replace("T", " ").slice(0, 16) : "—";
    },
  },
};
</script>
